import { ArrowRight, CircleAlert, Leaf, TrainFront, Umbrella } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { DataSection, NowResponse, StopsResponse } from '../../../shared/api/contract.ts';
import {
  AIR_QUALITY_LEVELS,
  summarizeNowcast,
  type AirQualityLevel,
  type Alert,
  type NetworkStatus,
} from '../../../shared/domain/index.ts';
import type { DataKind } from '../../../shared/freshness/index.ts';
import { DemoBadge, FreshnessBadge } from '../../components/FreshnessBadge.tsx';
import { sectionFreshness, unavailableMessage } from '../../components/freshness.ts';
import { LineBadge } from '../../components/LineBadge.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { SourceLine } from '../../components/SourceLine.tsx';
import { useNow } from '../../hooks/useNow.ts';
import { formatTemperature } from '../../lib/format.ts';
import { CONDITION_LABELS, nowcastText } from '../weather/conditions.ts';
import { WeatherIcon } from '../weather/WeatherIcon.tsx';

const NETWORK: Readonly<Record<NetworkStatus, { label: string; tone: 'ok' | 'warn' | 'danger' | 'muted' }>> = {
  normal: { label: 'Trafic normal', tone: 'ok' },
  disrupted: { label: 'Trafic perturbé', tone: 'warn' },
  heavily_disrupted: { label: 'Fortement perturbé', tone: 'danger' },
  unknown: { label: 'État inconnu', tone: 'muted' },
};

type Tone = 'weather' | 'rain' | 'air' | 'transport';

interface ConditionProps<T> {
  readonly tone: Tone;
  readonly label: string;
  readonly icon: ReactNode;
  readonly kind: DataKind;
  readonly section: DataSection<T> | undefined;
  readonly now: Date;
  readonly subject: string;
  readonly render: (data: T) => { value: ReactNode; detail?: ReactNode; extra?: ReactNode };
  readonly link?: { readonly to: string; readonly label: string };
}

/** Une carte de condition : autonome, avec son propre état (chargement, indisponible, ancien, démo). */
function Condition<T>({ tone, label, icon, kind, section, now, subject, render, link }: ConditionProps<T>) {
  const status = sectionFreshness(section, kind, now);
  const content =
    section !== undefined && section.data !== null && status !== 'unavailable' ? render(section.data) : null;
  const titleId = `condition-${tone}`;
  return (
    <section className={`condition condition--${tone}`} aria-labelledby={titleId} aria-busy={section === undefined}>
      <div className="condition__head">
        <span className="condition__icon">{icon}</span>
        <h3 className="condition__label" id={titleId}>
          {label}
        </h3>
        {section?.demo === true && <DemoBadge />}
        {status === 'stale' && <FreshnessBadge status="stale" />}
      </div>
      {section === undefined ? (
        <div aria-hidden="true">
          <span className="skeleton skeleton--value-lg" />
          <Skeleton width="70%" />
        </div>
      ) : content === null ? (
        <p className="condition__unavailable">{unavailableMessage(section, subject)}</p>
      ) : (
        <>
          <p className="condition__value">{content.value}</p>
          {content.detail !== undefined && <p className="condition__detail">{content.detail}</p>}
          {content.extra}
        </>
      )}
      {link !== undefined && (
        <Link to={link.to} className="condition__link">
          {link.label} <ArrowRight size={16} aria-hidden="true" />
        </Link>
      )}
    </section>
  );
}

/** Échelle ATMO officielle (6 niveaux) : la position du jour est mise en avant, sans valeur inventée. */
function AtmoScale({ level }: { level: AirQualityLevel }) {
  return (
    <div className="atmo-scale" aria-hidden="true">
      {([1, 2, 3, 4, 5, 6] as const).map((l) => (
        <span
          key={l}
          className={`atmo-scale__seg${l === level ? ' atmo-scale__seg--on' : ''}`}
          style={{ background: AIR_QUALITY_LEVELS[l].color }}
        />
      ))}
    </div>
  );
}

const SEVERITY_LABEL: Readonly<Record<Alert['severity'], string>> = {
  info: 'jaune',
  warning: 'orange',
  critical: 'rouge',
};

/** Vigilance active : bandeau coloré au-dessus des conditions (jamais masqué dans une carte). */
function VigilanceBanner({ alerts }: { alerts: readonly Alert[] }) {
  const worst = alerts.reduce<Alert['severity']>(
    (max, a) => (a.severity === 'critical' || (a.severity === 'warning' && max === 'info') ? a.severity : max),
    'info',
  );
  const source = alerts[0]?.source.sourceUrl;
  return (
    <div className={`vigilance vigilance--${worst}`} role="status">
      <CircleAlert size={22} aria-hidden="true" />
      <div>
        <p className="vigilance__title">
          {alerts.length === 1 ? alerts[0]?.title : `${alerts.length} vigilances en cours`}
          <span className="visually-hidden"> (niveau {SEVERITY_LABEL[worst]})</span>
        </p>
        <p className="vigilance__detail">
          {alerts.length === 1 ? alerts[0]?.message : alerts.map((a) => a.title).join(' · ')}
          {source !== undefined && (
            <>
              {' · '}
              <a href={source} rel="noopener noreferrer" target="_blank">
                Carte officielle Météo-France
              </a>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/** Sources de toutes les conditions, avec la mise à jour la plus ancienne (affichage conservateur). */
function combinedSources(data: NowResponse): DataSection<unknown> {
  const sections = [data.weather, data.nowcast, data.airQuality, data.network, data.alerts];
  const sources = [...new Map(sections.flatMap((s) => s.sources).map((s) => [s.id, s])).values()];
  const oldest = sections.flatMap((s) => (s.meta.fetchedAt === null ? [] : [s.meta.fetchedAt])).toSorted()[0];
  return {
    data: null,
    meta: { fetchedAt: oldest ?? null, observedAt: null, expiresAt: null },
    sources,
    demo: sections.some((s) => s.demo),
    error: null,
  };
}

export function ConditionsRow({
  data,
  lines,
}: {
  data: NowResponse | undefined;
  lines: StopsResponse['lines'] | undefined;
}) {
  const now = useNow(30_000);
  const alerts = data?.alerts.data ?? [];
  const alertsKnown = data !== undefined && data.alerts.data !== null;

  return (
    <section className="conditions" aria-labelledby="conditions-title">
      <h2 id="conditions-title" className="visually-hidden">
        Ermont maintenant
      </h2>
      {alerts.length > 0 && <VigilanceBanner alerts={alerts} />}
      <div className="conditions__grid">
        <Condition
          tone="weather"
          label="Météo"
          kind="weather"
          subject="météo"
          section={data?.weather}
          now={now}
          icon={
            <WeatherIcon
              condition={data?.weather.data?.condition ?? 'clear'}
              isDay={data?.weather.data?.isDay ?? true}
              size={26}
            />
          }
          render={(w) => ({
            value: formatTemperature(w.temperatureC),
            detail: (
              <>
                {CONDITION_LABELS[w.condition]}
                {w.feelsLikeC !== undefined && ` · ressenti ${formatTemperature(w.feelsLikeC)}`}
              </>
            ),
            extra: (
              <p className="condition__meta">
                {w.kind === 'model' ? 'Prévision Météo-France' : 'Mesure'}
                {alertsKnown && alerts.length === 0 && ' · aucune vigilance'}
              </p>
            ),
          })}
          link={{ to: '/ciel', label: 'Le ciel ce soir' }}
        />
        <Condition
          tone="rain"
          label="Pluie"
          kind="nowcast"
          subject="pluie"
          section={data?.nowcast}
          now={now}
          icon={<Umbrella size={26} aria-hidden="true" />}
          render={(n) => {
            const summary = summarizeNowcast(n, now);
            return summary.kind === 'dry'
              ? { value: 'Aucune pluie prévue', detail: `dans les ${summary.horizonMinutes} prochaines minutes` }
              : { value: nowcastText(summary), detail: 'Prévision à 15 minutes près' };
          }}
        />
        <Condition
          tone="air"
          label="Qualité de l’air"
          kind="airQuality"
          subject="qualité de l’air"
          section={data?.airQuality}
          now={now}
          icon={<Leaf size={26} aria-hidden="true" />}
          render={(a) => ({
            value: AIR_QUALITY_LEVELS[a.level].label,
            detail: <Link to="/air">Indice ATMO du jour, comment il est construit</Link>,
            extra: <AtmoScale level={a.level} />,
          })}
        />
        <Condition
          tone="transport"
          label="Transports"
          kind="disruptions"
          subject="transport"
          section={data?.network}
          now={now}
          icon={<TrainFront size={26} aria-hidden="true" />}
          render={(n) => ({
            value: (
              <>
                <span className={`status-dot status-dot--${NETWORK[n.status].tone}`} aria-hidden="true" />
                {NETWORK[n.status].label}
              </>
            ),
            detail:
              n.disruptions.length > 0
                ? `${n.disruptions.length} message${n.disruptions.length > 1 ? 's' : ''} sur les lignes`
                : undefined,
            extra:
              lines !== undefined && lines.length > 0 ? (
                <p className="condition__lines">
                  {lines.map((line) => (
                    <LineBadge key={line.id} line={line} size="sm" />
                  ))}
                </p>
              ) : undefined,
          })}
          link={{ to: '/transports', label: 'Voir les perturbations' }}
        />
      </div>
      {data !== undefined && <SourceLine section={combinedSources(data)} now={now} />}
    </section>
  );
}
